import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useUIStore } from '@/stores/uiStore';
import { CORE, BRAND, STATUS } from '@/utils/dls-tokens';

/** Build a standardised export filename: neeve-insights-<name>-YYMMDD_HHMMSS.<ext> */
export function exportFilename(name: string, ext: 'pdf' | 'csv'): string {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `neeve-insights-${name}-${yy}${mm}${dd}_${hh}${mi}${ss}.${ext}`;
}

const NEEVE_PRIMARY = CORE.neeveGreen;
const NEEVE_SECONDARY = CORE.darkGreen;
const NEEVE_ACCENT_BLUE = BRAND.tertiary;
const NEEVE_AMBER = STATUS.warning.primary;

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

// ── Layout ──────────────────────────────────────────────────────────────

/** Layout metrics returned by createBrandedPdf so callers know where to place content. */
export interface PdfLayout {
  doc: jsPDF;
  /** Left edge of the content area (after sidebar + gap). */
  left: number;
  /** Right margin from page edge. */
  right: number;
  /** Usable content width (pageWidth - left - right). */
  width: number;
  /** First Y position for content on page 1. */
  startY: number;
}

interface PdfOptions {
  title: string;
  subtitle?: string;
  format?: string;
  orientation?: 'landscape' | 'portrait';
  logoData?: string;
  isDemo?: boolean;
}

const MARGIN = 8;
const HEADER_H = 16;

/**
 * Loads the Neeve logo as a base64 data URL for PDF embedding.
 */
export async function loadLogoForPdf(): Promise<string> {
  const response = await fetch('/assets/logos/Neeve-Logo-Lockup-SM_flush_white.png');
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

/** Draw the branded header bar on the current page. */
function drawHeader(doc: jsPDF) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...hexToRgb(NEEVE_PRIMARY));
  doc.rect(0, 0, pageWidth, HEADER_H, 'F');
  // Thin accent line at bottom of header
  doc.setFillColor(...hexToRgb(NEEVE_SECONDARY));
  doc.rect(0, HEADER_H, pageWidth, 0.5, 'F');
}

/**
 * Creates a branded landscape jsPDF document with a top header bar.
 */
export function createBrandedPdf(options: PdfOptions): PdfLayout {
  const {
    title,
    subtitle,
    format = 'letter',
    orientation = 'landscape',
    logoData,
    isDemo: explicitDemo,
  } = options;
  const isDemo = explicitDemo ?? useUIStore.getState().session?.isDemo === true;
  const doc = new jsPDF({ orientation, unit: 'mm', format });
  const pageWidth = doc.internal.pageSize.getWidth();

  // ── Header bar ──
  drawHeader(doc);

  // Vertical center of header for aligning all elements
  const headerMid = HEADER_H / 2;
  const hPad = MARGIN;

  // Left side: logo on top, INSIGHTS + optional DEMO MODE below
  if (logoData) {
    const logoH = 6;
    const logoW = logoH * 4.21;
    const labelGap = 2; // gap between logo bottom and label top
    const labelH = 1.6; // approximate cap height of INSIGHTS at 5.5pt
    const blockH = logoH + labelGap + labelH;
    const blockTop = (HEADER_H - blockH) / 2;

    doc.addImage(logoData, 'PNG', hPad, blockTop, logoW, logoH);

    let textX = hPad;
    const labelY = blockTop + logoH + labelGap + labelH;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    doc.text('INSIGHTS', textX, labelY);

    if (isDemo) {
      textX += doc.getTextWidth('INSIGHTS') + 2;
      doc.setTextColor(...hexToRgb(NEEVE_AMBER));
      doc.setFontSize(4.5);
      doc.text('DEMO MODE', textX, labelY);
    }
  } else {
    // Text-only fallback: "neeve" + "INSIGHTS" vertically centered
    const neeveH = 3.5; // approximate cap height of "neeve" at 12pt
    const labelGap = 1.5;
    const labelH = 1.6;
    const blockH = neeveH + labelGap + labelH;
    const blockTop = (HEADER_H - blockH) / 2;

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('neeve', hPad, blockTop + neeveH);
    doc.setFontSize(5.5);
    doc.text('INSIGHTS', hPad, blockTop + neeveH + labelGap + labelH);
  }

  // Center: title + subtitle — baseline at vertical center + half cap height
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const titleText = subtitle ? `${title}  \u2014  ${subtitle}` : title;
  doc.text(titleText, pageWidth / 2, headerMid + 1.2, { align: 'center' });

  // Right: timestamp
  doc.setFontSize(6.5);
  doc.setTextColor(200, 200, 200);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth - hPad, headerMid + 1.2, {
    align: 'right',
  });

  // ── Content area ──
  const contentWidth = pageWidth - MARGIN * 2;
  const startY = HEADER_H + 2;

  return { doc, left: MARGIN, right: MARGIN, width: contentWidth, startY };
}

// ── Page helpers ────────────────────────────────────────────────────────

function addPageFooter(doc: jsPDF, pageNumber: number) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setDrawColor(...hexToRgb(NEEVE_ACCENT_BLUE));
  doc.setLineWidth(0.3);
  doc.line(MARGIN, pageHeight - 10, pageWidth - MARGIN, pageHeight - 10);

  doc.setFontSize(6);
  doc.setTextColor(150, 150, 150);
  doc.text('Neeve Insights \u2014 Confidential', MARGIN, pageHeight - 6);
  doc.text(`Page ${pageNumber}`, pageWidth - MARGIN, pageHeight - 6, { align: 'right' });
}

/**
 * Checks if there's enough space on the current page; if not, adds a new page
 * and returns the top-of-content Y.  Header only appears on page 1.
 */
export function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - 16) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

// ── Screenshot image helpers (used by Dashboard export) ────────────────

export function addScaledImage(
  doc: jsPDF,
  imgData: string,
  y: number,
  captureWidth: number,
  captureHeight: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  const w = layout?.width ?? doc.internal.pageSize.getWidth() - left - MARGIN;
  const scaledHeight = w * (captureHeight / captureWidth);

  y = ensureSpace(doc, y, Math.min(scaledHeight + 4, 60));
  doc.addImage(imgData, 'PNG', left, y, w, scaledHeight);

  return y + scaledHeight + 4;
}

export function addSideBySideImages(
  doc: jsPDF,
  left: { imgData: string; width: number; height: number },
  right: { imgData: string; width: number; height: number },
  y: number,
  layout?: PdfLayout
): number {
  const ml = layout?.left ?? MARGIN;
  const totalW = layout?.width ?? doc.internal.pageSize.getWidth() - ml - MARGIN;
  const gap = 4;
  const halfWidth = (totalW - gap) / 2;

  const leftHeight = halfWidth * (left.height / left.width);
  const rightHeight = halfWidth * (right.height / right.width);
  const maxHeight = Math.max(leftHeight, rightHeight);

  y = ensureSpace(doc, y, Math.min(maxHeight + 4, 60));

  doc.addImage(left.imgData, 'PNG', ml, y, halfWidth, leftHeight);
  doc.addImage(right.imgData, 'PNG', ml + halfWidth + gap, y, halfWidth, rightHeight);

  return y + maxHeight + 4;
}

// ── Table / metric helpers (used by other export pages) ────────────────

export function addBrandedTable(
  doc: jsPDF,
  headers: string[],
  rows: (string | number | null | undefined)[][],
  startY?: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  const right = layout?.right ?? MARGIN;

  autoTable(doc, {
    head: [headers],
    body: rows.map((row) =>
      row.map((cell) => (cell === null || cell === undefined ? '' : String(cell)))
    ),
    startY: startY ?? 10,
    headStyles: {
      fillColor: hexToRgb(NEEVE_SECONDARY),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [50, 50, 50],
    },
    alternateRowStyles: {
      fillColor: [245, 250, 249],
    },
    styles: {
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    margin: { left, right },
    didDrawPage: (data) => {
      addPageFooter(doc, data.pageNumber);
    },
  });

  return (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 10;
}

export function addMetricRow(
  doc: jsPDF,
  metrics: { label: string; value: string }[],
  y: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  const w = layout?.width ?? doc.internal.pageSize.getWidth() - left - MARGIN;
  const boxWidth = w / metrics.length;
  const boxHeight = 18;

  metrics.forEach((metric, i) => {
    const x = left + i * boxWidth;

    doc.setFillColor(245, 250, 249);
    doc.roundedRect(x, y, boxWidth - 4, boxHeight, 2, 2, 'F');

    doc.setTextColor(...hexToRgb(NEEVE_PRIMARY));
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(metric.value, x + (boxWidth - 4) / 2, y + 8, { align: 'center' });

    doc.setTextColor(100, 100, 100);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(metric.label, x + (boxWidth - 4) / 2, y + 14, { align: 'center' });
  });

  return y + boxHeight + 6;
}

export function addSectionHeading(doc: jsPDF, text: string, y: number, layout?: PdfLayout): number {
  const left = layout?.left ?? MARGIN;
  doc.setFillColor(...hexToRgb(NEEVE_ACCENT_BLUE));
  doc.rect(left, y, 30, 1, 'F');

  doc.setTextColor(...hexToRgb(NEEVE_PRIMARY));
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(text, left, y + 8);

  return y + 14;
}

export function addCompactSummaryRow(
  doc: jsPDF,
  items: { label: string; value: number; color?: string }[],
  y: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  const nonZero = items.filter((item) => item.value > 0);

  if (nonZero.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'dls-italic');
    doc.text('No active issues', left, y + 5);
    return y + 12;
  }

  const badgeHeight = 8;
  const badgePadding = 3;
  let x = left;

  nonZero.forEach((item) => {
    const text = `${item.value} ${item.label}`;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const textWidth = doc.getTextWidth(text);
    const badgeWidth = textWidth + badgePadding * 2;

    const rgb = hexToRgb(item.color || NEEVE_SECONDARY);
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.roundedRect(x, y, badgeWidth, badgeHeight, 1.5, 1.5, 'F');

    doc.setTextColor(255, 255, 255);
    doc.text(text, x + badgePadding, y + 5.5);

    x += badgeWidth + 3;
  });

  return y + badgeHeight + 6;
}

export function addHealthScoreBreakdown(
  doc: jsPDF,
  score: number,
  components: { label: string; score: number; weight: string; detail: string }[],
  actions: string[],
  y: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  const pageWidth = doc.internal.pageSize.getWidth();
  const right = layout?.right ?? MARGIN;
  const barAreaWidth = pageWidth - left - right;

  const estimatedHeight =
    8 + components.length * 12 + (actions.length > 0 ? 8 + actions.length * 5 : 0);
  y = ensureSpace(doc, y, estimatedHeight);

  const scoreColor = score >= 90 ? STATUS.success.primary : score >= 70 ? STATUS.warning.primary : STATUS.danger.primary;
  doc.setTextColor(...hexToRgb(scoreColor));
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(`${score.toFixed(1)}`, left, y + 6);
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text('/ 100', left + doc.getTextWidth(`${score.toFixed(1)} `), y + 6);

  y += 12;

  components.forEach((comp) => {
    const pct = Math.min(100, Math.max(0, comp.score));
    const barColor = pct >= 95 ? STATUS.success.primary : pct >= 80 ? STATUS.warning.primary : STATUS.danger.primary;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(NEEVE_PRIMARY));
    doc.text(comp.label, left, y);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`(${comp.weight})`, left + doc.getTextWidth(comp.label + ' '), y);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(barColor));
    doc.text(`${pct.toFixed(1)}%`, pageWidth - right, y, { align: 'right' });

    y += 3;

    doc.setFillColor(230, 230, 230);
    doc.roundedRect(left, y, barAreaWidth, 3, 1, 1, 'F');

    doc.setFillColor(...hexToRgb(barColor));
    const fillWidth = Math.max(0, (pct / 100) * barAreaWidth);
    if (fillWidth > 0) {
      doc.roundedRect(left, y, fillWidth, 3, 1, 1, 'F');
    }

    y += 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(comp.detail, left, y);

    y += 5;
  });

  if (actions.length > 0) {
    y += 2;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(NEEVE_PRIMARY));
    doc.text('Recommended Actions', left, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    actions.forEach((action) => {
      y = ensureSpace(doc, y, 6);
      doc.text(`\u2022  ${action}`, left + 2, y);
      y += 4.5;
    });
  }

  return y + 4;
}

export function addUptimeStatsBlock(
  doc: jsPDF,
  stats: {
    avgUptimeDays: number;
    longestUptimeDays: number;
    longestUptimeNodeName: string | null;
    shortestUptimeDays: number;
    shortestUptimeNodeName: string | null;
  },
  title: string,
  y: number,
  formatDaysFn: (days: number) => string,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  y = ensureSpace(doc, y, 30);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(NEEVE_SECONDARY));
  doc.text(title, left, y);
  y += 5;

  const items = [
    { label: 'Average Uptime', value: formatDaysFn(stats.avgUptimeDays) },
    {
      label: 'Longest Uptime',
      value: `${formatDaysFn(stats.longestUptimeDays)}${stats.longestUptimeNodeName ? ` (${stats.longestUptimeNodeName})` : ''}`,
    },
    {
      label: 'Shortest Uptime',
      value: `${formatDaysFn(stats.shortestUptimeDays)}${stats.shortestUptimeNodeName ? ` (${stats.shortestUptimeNodeName})` : ''}`,
    },
  ];

  return addMetricRow(doc, items, y, layout);
}

export function addAlertTable(
  doc: jsPDF,
  alerts: {
    type: string;
    severity: string;
    nodeName: string;
    message: string;
    since?: string;
    lastContact?: string;
  }[],
  y: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;

  if (alerts.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'dls-italic');
    doc.text('No active alerts', left, y + 5);
    return y + 12;
  }

  const types = ['offline', 'outdated', 'flapping', 'stale'] as const;
  for (const type of types) {
    const group = alerts.filter((a) => a.type === type);
    if (group.length === 0) continue;

    y = ensureSpace(doc, y, 20);

    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(NEEVE_PRIMARY));
    doc.text(`${typeLabel} (${group.length})`, left, y);
    y += 4;

    y = addBrandedTable(
      doc,
      ['Node', 'Message', 'Since', 'Last Contact'],
      group.map((a) => [
        a.nodeName,
        a.message,
        a.since ? new Date(a.since).toLocaleDateString() : '\u2014',
        a.lastContact ? new Date(a.lastContact).toLocaleDateString() : '\u2014',
      ]),
      y,
      layout
    );
    y += 4;
  }

  return y;
}

export function addServiceTable(
  doc: jsPDF,
  categories: {
    label: string;
    services: {
      displayName: string;
      nodeName: string;
      statusReason: string | null;
      totalRestartCount: number;
    }[];
  }[],
  y: number,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  let hasAny = false;
  for (const cat of categories) {
    if (cat.services.length === 0) continue;
    hasAny = true;

    y = ensureSpace(doc, y, 20);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(NEEVE_PRIMARY));
    doc.text(`${cat.label} (${cat.services.length})`, left, y);
    y += 4;

    y = addBrandedTable(
      doc,
      ['Service', 'Node', 'Reason', 'Restarts'],
      cat.services.map((s) => [
        s.displayName,
        s.nodeName,
        s.statusReason || '\u2014',
        s.totalRestartCount,
      ]),
      y,
      layout
    );
    y += 4;
  }

  if (!hasAny) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'dls-italic');
    doc.text('All services healthy', left, y + 5);
    return y + 12;
  }

  return y;
}

export function addChartImage(
  doc: jsPDF,
  imgData: string,
  y: number,
  maxHeight = 60,
  layout?: PdfLayout
): number {
  const left = layout?.left ?? MARGIN;
  const w = layout?.width ?? doc.internal.pageSize.getWidth() - left - MARGIN;

  y = ensureSpace(doc, y, maxHeight + 10);
  doc.addImage(imgData, 'PNG', left, y, w, maxHeight);

  return y + maxHeight + 6;
}

export function addOfflineNodesTable(
  doc: jsPDF,
  nodes: {
    name: string;
    state: string;
    portfolio?: string | null;
    profileName?: string | null;
    softwareVersion?: string | null;
    lastContactedAt?: string | null;
  }[],
  y: number,
  layout?: PdfLayout
): number {
  if (nodes.length === 0) return y;

  y = addBrandedTable(
    doc,
    ['Node', 'State', 'Portfolio', 'Profile', 'Version', 'Last Contact'],
    nodes.map((n) => [
      n.name,
      n.state,
      n.portfolio || '\u2014',
      n.profileName || '\u2014',
      n.softwareVersion || '\u2014',
      n.lastContactedAt ? new Date(n.lastContactedAt).toLocaleString() : '\u2014',
    ]),
    y,
    layout
  );

  return y + 4;
}

/**
 * Saves and downloads the PDF.
 */
export function downloadPdf(doc: jsPDF, filename: string) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addPageFooter(doc, i);
  }

  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
